import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { getSalesHistoricSellers, SalesHistoricSellersParams } from "@/Api/sales";
import { SellersHistoricTable } from "./Table";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, Search, X } from "lucide-react";
import { format, subDays } from "date-fns";
import { cn } from "@/lib/utils";
import { getSelectSellers } from "@/Api/sellers";
import { SALE_STATUS_LABELS } from "@/utils/constants";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface SellerHistoricData {
  seller_id: number;
  seller: string;
  total_vendido: string;
}

interface HistoricResponse {
  start_date: string;
  end_date: string;
  sellers: SellerHistoricData[];
  total_geral: string;
}

export function SellersHistoric() {
  const [dataApi, setDataApi] = useState<HistoricResponse | null>(null);
  const [filters, setFilters] = useState<SalesHistoricSellersParams>({
    start_date: format(subDays(new Date(), 30), "yyyy-MM-dd"),
    end_date: format(new Date(), "yyyy-MM-dd"),
  });
  const [loading, setLoading] = useState(false);
  const [sellers, setSellers] = useState<Array<{ value: string; label: string }>>([]);
  const [startDate, setStartDate] = useState<Date>(subDays(new Date(), 30));
  const [endDate, setEndDate] = useState<Date>(new Date());


  const updateArrayFilter = (key: keyof SalesHistoricSellersParams, value: string, action: 'add' | 'remove') => {
    const currentValues = (filters[key] as string[] | undefined) || [];
    let newValues: string[];

    if (action === 'add') {
      if (!currentValues.includes(value)) {
        newValues = [...currentValues, value];
      } else {
        return;
      }
    } else {
      newValues = currentValues.filter(v => v !== value);
    }

    setFilters({
      ...filters,
      [key]: newValues.length > 0 ? newValues : undefined,
    });
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getSalesHistoricSellers({
        ...filters,
        start_date: format(startDate, "yyyy-MM-dd"),
        end_date: format(endDate, "yyyy-MM-dd"),
      });
      
      if (data?.response) {
        setDataApi(data.response);
      }
    } catch (error) {
      toast.error("Erro ao carregar dados dos vendedores");
      console.error("Erro:", error);
    } finally {
      setLoading(false);
    }
  }, [filters, startDate, endDate]);

  const fetchSellers = async () => {
    try {
      const { data } = await getSelectSellers({ is_active: true });
      if (data?.response) {
        const sellerOptions = data.response.map((seller: any) => ({
          value: seller.id.toString(),
          label: seller.user?.name || seller.name,
        }));
        setSellers(sellerOptions);
      }
    } catch (error) {
      console.error("Erro ao carregar vendedores:", error);
    }
  };

  useEffect(() => {
    fetchSellers();
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = () => {
    fetchData();
  };

  return (
    <div className="w-full space-y-6">
      <div className="flex flex-col gap-4 p-4 bg-white dark:bg-zinc-800 rounded-lg shadow">
        <h2 className="text-lg font-semibold">Filtros</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Data Início</label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !startDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {startDate ? format(startDate, "dd/MM/yyyy") : "Selecione"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={startDate}
                  onSelect={(date) => date && setStartDate(date)}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Data Fim</label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !endDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {endDate ? format(endDate, "dd/MM/yyyy") : "Selecione"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={endDate}
                  onSelect={(date) => date && setEndDate(date)}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Vendedor</label>
            {filters.seller_id && filters.seller_id.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {filters.seller_id.map((sellerId) => {
                  const seller = sellers.find(s => s.value === sellerId);
                  return (
                    <Badge key={sellerId} variant="secondary" className="flex items-center gap-1">
                      {seller?.label}
                      <X
                        className="h-3 w-3 cursor-pointer hover:text-red-500"
                        onClick={() => updateArrayFilter("seller_id", sellerId, "remove")}
                      />
                    </Badge>
                  );
                })}
              </div>
            )}
            <Select
              value=""
              onValueChange={(value) => {
                if (value) {
                  updateArrayFilter("seller_id", value, "add");
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Todos os vendedores" />
              </SelectTrigger>
              <SelectContent>
                {sellers.map((seller) => {
                  const isSelected = filters.seller_id?.includes(seller.value);
                  return (
                    <SelectItem
                      key={seller.value}
                      value={seller.value}
                      disabled={isSelected}
                    >
                      {seller.label} {isSelected && "(Selecionado)"}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Status</label>
            {filters.sale_status_id && filters.sale_status_id.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {filters.sale_status_id.map((statusId) => {
                  const statusLabel = SALE_STATUS_LABELS[Number(statusId)];
                  return (
                    <Badge key={statusId} variant="secondary" className="flex items-center gap-1">
                      {statusLabel}
                      <X
                        className="h-3 w-3 cursor-pointer hover:text-red-500"
                        onClick={() => updateArrayFilter("sale_status_id", statusId, "remove")}
                      />
                    </Badge>
                  );
                })}
              </div>
            )}
            <Select
              value=""
              onValueChange={(value) => {
                if (value) {
                  updateArrayFilter("sale_status_id", value, "add");
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Todos os status" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(SALE_STATUS_LABELS).map(([value, label]) => {
                  const isSelected = filters.sale_status_id?.includes(value);
                  return (
                    <SelectItem
                      key={value}
                      value={value}
                      disabled={isSelected}
                    >
                      {label} {isSelected && "(Selecionado)"}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-end">
            <Button
              onClick={handleSearch}
              disabled={loading}
              className="w-full"
            >
              <Search className="mr-2 h-4 w-4" />
              {loading ? "Buscando..." : "Buscar"}
            </Button>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-zinc-800 rounded-lg shadow">
        <div className="p-4 border-b">
          <h1 className="text-xl font-semibold">Histórico de Vendas por Vendedor</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Visualize o histórico de vendas e performance dos vendedores
          </p>
        </div>
        
        <div className="p-4">
          <SellersHistoricTable
            dataApi={dataApi}
          />
        </div>
      </div>
    </div>
  );
}